import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import dayjs from "dayjs";

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newItem, setNewItem] = useState({ title: "", text: "" });
  const [submitting, setSubmitting] = useState(false);

  // 🧾 Načti příspěvky (GET)
  const fetchAnnouncements = async () => {
    if (!user?.firma) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/dashboard/${user.firma}/announcements`, {
        method: "GET",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při načítání příspěvků");

      setAnnouncements(data);
      setError(null);
    } catch (err) {
      console.error("Chyba při načítání nástěnky:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [user]);

  // ➕ Přidání nového příspěvku (POST)
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newItem.title || !newItem.text) return;

    setSubmitting(true);
    try {
      const res = await fetch(`/api/dashboard/${user.firma}/announcements`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newItem),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Nepodařilo se přidat příspěvek");

      setNewItem({ title: "", text: "" });
      await fetchAnnouncements();
    } catch (err) {
      console.error("Chyba při přidávání:", err);
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ❌ Smazání příspěvku (DELETE)
  const handleDelete = async (id) => {
    if (!window.confirm("Opravdu smazat tento příspěvek?")) return;

    try {
      const res = await fetch(`/api/dashboard/${user.firma}/announcements/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chyba při mazání");

      setAnnouncements((prev) => prev.filter((a) => a._id !== id));
    } catch (err) {
      console.error("Mazání příspěvku:", err);
      alert(err.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6 text-blue-800">
      {/* 🧭 Levá část: Administrace */}
      <div className="lg:col-span-2 space-y-6">
        {/* ✏️ Správa nástěnky */}
        <div className="bg-white rounded-2xl shadow p-6 border">
          <h2 className="text-xl font-bold mb-4">📝 Správa nástěnky</h2>

          {/* Formulář pro nový příspěvek */}
          <form onSubmit={handleAdd} className="space-y-3 mb-6">
            <input
              type="text"
              placeholder="Titulek příspěvku"
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              className="w-full border rounded-lg p-2"
            />
            <textarea
              placeholder="Text příspěvku"
              value={newItem.text}
              onChange={(e) => setNewItem({ ...newItem, text: e.target.value })}
              rows={3}
              className="w-full border rounded-lg p-2"
            ></textarea>
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              {submitting ? "Ukládám..." : "Přidat příspěvek"}
            </button>
          </form>

          {/* Seznam příspěvků */}
          {loading ? (
            <p>Načítání příspěvků...</p>
          ) : error ? (
            <p className="text-red-600">Chyba: {error}</p>
          ) : announcements.length === 0 ? (
            <p className="text-gray-500 italic">Zatím žádné příspěvky.</p>
          ) : (
            <div className="space-y-4">
              {announcements.map((a) => (
                <div key={a._id} className="border-l-4 border-green-500 pl-4 relative">
                  <button
                    onClick={() => handleDelete(a._id)}
                    className="absolute top-1 right-2 text-red-500 hover:text-red-700 text-sm"
                    title="Smazat"
                  >
                    ✕
                  </button>
                  <h4 className="text-gray-800 font-semibold">{a.title}</h4>
                  <p className="text-sm text-gray-500 mb-1">
                    {dayjs(a.date).format("D. M. YYYY")} — {a.author || "Admin"}
                  </p>
                  <p className="text-gray-600 whitespace-pre-line">{a.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
